'use client'

import { DELIVERY_METHODS } from 'my-purchases/constants/deliveryMethods'
import { ORDER_STATUS } from 'my-purchases/constants/orderStatus'
import { ORDER_STEPS } from 'my-purchases/constants/orderSteps'
import type { EventDates } from 'my-purchases/types/purchaseStatus'
import { useMemo } from 'react'

import { OrderTrackerStep } from './content/OrderTrackerStep/OrderTrackerStep'

import styles from './OrderTrackerSteps.module.scss'

interface OrderTrackerStepsProps {
	readonly orderStatus: ORDER_STATUS
	readonly deliveryMethod?: DELIVERY_METHODS
	readonly eventDates?: EventDates
}

export function OrderTrackerSteps({
	orderStatus,
	deliveryMethod,
	eventDates,
}: OrderTrackerStepsProps) {
	const getStepIndex = (target: ORDER_STATUS) =>
		ORDER_STEPS.findIndex((orderStep) => orderStep.includes(target))

	const orderStatusIdx = getStepIndex(orderStatus)

	const buildSteps = useMemo(() => {
		const steps = [ORDER_STATUS.created]

		// Always show latest status for current or subsequent statuses
		if (
			orderStatus === ORDER_STATUS.ready ||
			orderStatusIdx > getStepIndex(ORDER_STATUS.ready)
		) {
			steps.push(ORDER_STATUS.ready)
		} else {
			steps.push(ORDER_STATUS.in_preparation)
		}

		// Special shipping status
		if (orderStatus === ORDER_STATUS.on_delivery) {
			steps.push(ORDER_STATUS.on_delivery)
		} else if (orderStatus === ORDER_STATUS.on_international_transit) {
			steps.push(ORDER_STATUS.on_international_transit)
		} else {
			steps.push(ORDER_STATUS.shipped)
		}

		// Special delivery options that require an extra step
		if (deliveryMethod === DELIVERY_METHODS.store_delivery) {
			steps.push(ORDER_STATUS.in_store)
		}
		if (deliveryMethod === DELIVERY_METHODS.droppoint_delivery) {
			steps.push(ORDER_STATUS.in_droppoint)
		}

		steps.push(ORDER_STATUS.delivered)

		return steps
	}, [orderStatus, deliveryMethod])

	return (
		<ul
			className={styles.container}
			data-testid='myPurchases.purchaseDetail.orderTracker.steps'
		>
			{buildSteps.map((step: ORDER_STATUS) => (
				<OrderTrackerStep
					key={step}
					orderStatusIdx={orderStatusIdx}
					stepStatus={step}
					stepStatusIdx={getStepIndex(step)}
					eventDate={eventDates && eventDates[step]}
				/>
			))}
		</ul>
	)
}
